import { ActionIcon, Alert, Anchor, Badge, Box, Button, Drawer, Group, HoverCard, Loader, SegmentedControl, Stack, Table, Tabs, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { IconArrowsDiagonal, IconArrowsDiagonalMinimize2, IconChevronUp, IconHistory, IconLock } from "@tabler/icons-react";
import { fmtNumber, matchPassioRoute, RESPONSE_HEADER_DOCS, type PassioCallRecord } from "@passio/api-client";
import { executionTokens, sinkOutput, TERMINAL_STATUSES, type ChainCallRecord, type NodeResult } from "@passio/easyai-client";
import { clearCalls, closeInspector, isChainCall, openInspector, useCallHistory, useInspector, type CallRecord } from "../lib/calls";
import { CHAIN_CODE_LANGS as CODE_LANGS, chainCode, type ChainCodeLang as CodeLang } from "../lib/chainCode";
import { easyAiOrigin, passio, publicApiBase } from "../lib/clients";
import { useSession } from "../lib/session";
import { createStore, useStore } from "../lib/store";
import { toCurl, toFetch, toPython, toTypeScript } from "../lib/codegen";
import { chainExchange, passioExchange } from "../lib/exchange";
import { CodeBlock, JsonView } from "./JsonView";
import type { CodeLanguage } from "./highlight";
import { ErrorNotice } from "./common";
import { AccountButton } from "./AccountCta";

/** Snippet language, shared by API and chain calls so a choice sticks while browsing history. */
const codeLang = createStore<CodeLang>("curl");
const useCodeLang = () => useStore(codeLang);
const CODE_LANGUAGE: Record<CodeLang, CodeLanguage> = { curl: "shell", ts: "ts", fetch: "ts", python: "python" };

const fileStem = (r: CallRecord) =>
  (isChainCall(r) ? (r.chainKey ?? "chain") : (matchPassioRoute(r.method, r.path)?.id ?? "call")).replace(/[^\w-]+/g, "-") + `-${r.startedAt.slice(0, 19).replace(/[:T]/g, "")}`;

const chainRunning = (r: ChainCallRecord) => !TERMINAL_STATUSES.includes(r.status) && r.status !== "error" && r.status !== "stopped";

function statusColor(r: CallRecord) {
  if (isChainCall(r)) return r.status === "completed" ? "lime.7" : chainRunning(r) ? "blue" : r.status === "stopped" || r.status === "cancelled" ? "orange" : "red";
  return r.status === 0 ? "gray" : r.ok ? "lime.7" : r.status === 429 ? "orange" : "red";
}

function statusLabel(r: CallRecord) {
  if (isChainCall(r)) return r.status === "completed" ? "done" : chainRunning(r) ? "running" : r.status === "error" ? String(r.httpStatus ?? "ERR") : r.status;
  return r.status ? String(r.status) : "ERR";
}

function callTitle(r: CallRecord) {
  if (isChainCall(r)) return `${r.label ?? r.chainKey ?? "Chain"} · EasyAI chain`;
  return matchPassioRoute(r.method, r.path)?.title ?? r.path;
}

const callTokens = (r: CallRecord) => (isChainCall(r) ? (r.execution ? executionTokens(r.execution) : undefined) : (r.meta?.requestUsage ?? r.meta?.billedTokens.total)) ?? 0;

const fmtDuration = (ms: number) => (ms < 10_000 ? `${fmtNumber(ms)} ms` : `${(ms / 1000).toFixed(1)} s`);

const executePath = (r: ChainCallRecord) => `/api/v2/chains/${r.chainId}/execute`;

function HeaderTable({ rows }: { rows: [string, string, string?][] }) {
  if (!rows.length) return <Text c="dimmed" fz="sm">None</Text>;
  return (
    <Table fz="xs" verticalSpacing={6} withRowBorders highlightOnHover>
      <Table.Tbody>
        {rows.map(([k, v, hint]) => (
          <Table.Tr key={k}>
            <Table.Td style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
              <Tooltip label={hint} disabled={!hint} multiline w={260}>
                <Text fz="xs" ff="monospace" fw={600}>
                  {k}
                </Text>
              </Tooltip>
            </Table.Td>
            <Table.Td>
              <Text fz="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                {v}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function MetaGrid({ items }: { items: [string, string][] }) {
  return (
    <Box style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
      {items.map(([k, v]) => (
        <Box key={k} p="xs" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 10, background: "white" }}>
          <Text fz={11} c="dimmed" ff="monospace">
            {k}
          </Text>
          <Text fz="sm" fw={650} style={{ wordBreak: "break-all" }}>
            {v}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

function passioMeta(r: PassioCallRecord): [string, string][] {
  const m = r.meta;
  return [
    ["Status", r.status ? String(r.status) : "network error"],
    ["Duration", `${fmtNumber(r.durationMs)} ms`],
    ["Tokens charged", fmtNumber(m?.requestUsage ?? m?.billedTokens.total)],
    ["Request ID", m?.requestId ?? "–"],
    [
      "Model",
      m?.modelRequested && m.modelTag && m.modelRequested !== m.modelTag
        ? `${m.modelTag} (asked for ${m.modelRequested})`
        : (m?.modelTag ?? m?.modelRequested ?? "–"),
    ],
    ["Est. cost", m?.estimatedCostUsd !== undefined ? `$${m.estimatedCostUsd.toFixed(5)}` : "–"],
  ];
}

/** `GET /napi/feedback/{requestId}`: what Passio recorded for an AI call (tokens, cost, model, Easy AI runs) and its feedback. */
function TraceTab({ requestId }: { requestId: string }) {
  const lookup = useMutation({ mutationFn: () => passio.feedback.lookup(requestId, { quiet: true }) });
  const t = lookup.data?.data.trace;
  const feedback = lookup.data?.data.feedback ?? [];
  const snippet = [
    `curl ${publicApiBase()}/napi/feedback/${requestId} \\`,
    `  -H "Authorization: Bearer $PASSIO_TOKEN" \\`,
    `  -H "Passio-ID: $PASSIO_CUSTOMER_ID"`,
  ].join("\n");
  return (
    <Stack gap="sm">
      <Text fz="xs" c="dimmed">
        Look up what Passio recorded for this request id: route, model, tokens, estimated cost, linked Easy AI runs and any feedback sent. Traces exist for AI routes, appear a few seconds after the response and are kept for 180 days. Not billed.
      </Text>
      <Group gap="xs">
        <Button size="xs" variant="light" onClick={() => lookup.mutate()} loading={lookup.isPending}>
          {lookup.data ? "Look up again" : "Look up trace"}
        </Button>
      </Group>
      {lookup.error ? <ErrorNotice error={lookup.error} /> : null}
      {lookup.data && !t && (
        <Alert color="gray" variant="light">
          No trace yet. It can take a few seconds to appear; try again shortly.
        </Alert>
      )}
      {t && (
        <MetaGrid
          items={[
            ["Route", t.route ?? "–"],
            ["Model", t.modelTag ?? "–"],
            ["Billed tokens", fmtNumber(t.billedTokens)],
            ["In / out tokens", `${fmtNumber(t.inputTokens)} / ${fmtNumber(t.outputTokens)}`],
            ["Est. cost", t.estimatedCostUsd !== undefined ? `$${t.estimatedCostUsd.toFixed(5)}` : "–"],
            ["Duration", t.durationMs !== undefined ? fmtDuration(t.durationMs) : "–"],
            ["Easy AI runs", String(t.easyaiRuns?.length ?? 0)],
            ["Feedback items", String(feedback.length)],
            ["Avg score", t.avgScore !== undefined && t.avgScore !== null ? String(t.avgScore) : "–"],
          ]}
        />
      )}
      {lookup.data && <JsonView value={lookup.data.data} defaultMode="raw" maxHeight="40vh" fileName={`trace-${requestId}.json`} />}
      <CodeBlock code={snippet} language="shell" />
    </Stack>
  );
}

const NUTRITION_ROUTES = new Set(["byRefCode", "byProductCode", "semanticSearch", "advancedSearch", "linkIngredients", "extractIngredients", "extractIngredientsAutoTyped"]);

function CallDetail({ r }: { r: PassioCallRecord }) {
  const lang = useCodeLang();
  const [headerView, setHeaderView] = useState("table");
  const { toolEnabled, me } = useSession();
  const docs = new Map(RESPONSE_HEADER_DOCS.map((d) => [d.name.toLowerCase(), d.description]));
  const traceable = Boolean(matchPassioRoute(r.method, r.path)?.ai && r.meta?.requestId);
  const code = lang === "curl" ? toCurl(r) : lang === "ts" ? toTypeScript(r) : lang === "python" ? toPython(r) : toFetch(r);
  const respRows = Object.entries(r.meta?.raw ?? {})
    .filter(([k]) => k.startsWith("x-") || k === "content-type")
    .sort(([a], [b]) => Number(docs.has(b)) - Number(docs.has(a)))
    .map(([k, v]) => [k, v, docs.get(k)] as [string, string, string?]);
  return (
    <Stack gap="md">
      <Group gap="xs" wrap="nowrap">
        <Badge color={statusColor(r)} variant="filled" style={{ flex: "none" }}>
          {statusLabel(r)}
        </Badge>
        <Badge variant="outline" color="ink" style={{ flex: "none" }}>
          {r.method}
        </Badge>
        <Text ff="monospace" fz="xs" style={{ wordBreak: "break-all" }}>
          {r.url.replace(/^\/proxy\/passio/, "")}
        </Text>
      </Group>
      <MetaGrid items={passioMeta(r)} />
      {r.error && (
        <Text c="red" fz="sm">
          {r.error}
        </Text>
      )}
      <Tabs defaultValue="response" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="response">Response</Tabs.Tab>
          <Tabs.Tab value="headers">Headers</Tabs.Tab>
          <Tabs.Tab value="request">Request</Tabs.Tab>
          <Tabs.Tab value="code">Code</Tabs.Tab>
          <Tabs.Tab value="full">Full JSON</Tabs.Tab>
          {traceable && <Tabs.Tab value="trace">Trace</Tabs.Tab>}
        </Tabs.List>
        {traceable && r.meta?.requestId && (
          <Tabs.Panel value="trace" pt="sm">
            <TraceTab requestId={r.meta.requestId} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="response" pt="sm">
          {r.data === undefined ? <Text c="dimmed">No body</Text> : <JsonView value={r.data} maxHeight="60vh" fileName={`${fileStem(r)}-response.json`} />}
        </Tabs.Panel>
        <Tabs.Panel value="headers" pt="sm">
          <SegmentedControl size="xs" mb="sm" value={headerView} onChange={setHeaderView} data={[{ value: "table", label: "Table" }, { value: "json", label: "JSON" }]} />
          {headerView === "json" ? (
            <JsonView
              value={{ response: Object.fromEntries(respRows.map(([k, v]) => [k, v])), request: r.requestHeaders }}
              defaultMode="raw"
              maxHeight="60vh"
              fileName={`${fileStem(r)}-headers.json`}
            />
          ) : (
            <>
              <Text fw={650} fz="sm" mb={4}>
                Response headers
              </Text>
              <HeaderTable rows={respRows} />
              <Text fw={650} fz="sm" mt="md" mb={4}>
                Request headers (auth injected by the proxy)
              </Text>
              <HeaderTable rows={Object.entries(r.requestHeaders).map(([k, v]) => [k, v])} />
            </>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="request" pt="sm">
          {r.requestBody === undefined ? <Text c="dimmed">No request body (GET)</Text> : <JsonView value={r.requestBody} maxHeight="60vh" fileName={`${fileStem(r)}-request.json`} />}
        </Tabs.Panel>
        <Tabs.Panel value="full" pt="sm">
          <Text fz="xs" c="dimmed" mb={6}>
            The whole exchange as it looks against the API directly: URL, every header and both bodies. Credentials are placeholders.
          </Text>
          <JsonView value={passioExchange(r)} defaultMode="raw" maxHeight="60vh" fileName={`${fileStem(r)}-exchange.json`} />
        </Tabs.Panel>
        <Tabs.Panel value="code" pt="sm">
          <SegmentedControl size="xs" mb="sm" value={lang} onChange={(v) => codeLang.set(v as CodeLang)} data={CODE_LANGS} />
          <CodeBlock code={code} language={CODE_LANGUAGE[lang]} />
          {me?.user.authKind !== "api_key" && (
            <Group mt="sm" p="xs" justify="space-between" gap="xs" style={{ background: "#f3fbe9", border: "1px solid var(--pd-lime)", borderRadius: 10 }}>
              <Text fz="xs">Run this from your own app: create your Passio account to get the API key it needs.</Text>
              <AccountButton placement="inspector" size="xs">
                Get your API key
              </AccountButton>
            </Group>
          )}
          {NUTRITION_ROUTES.has(matchPassioRoute(r.method, r.path)?.id ?? "") && toolEnabled("nutrition_math") && (
            <Text fz="xs" c="dimmed" mt="sm">
              Turning this response into per-portion numbers happens in your app.{" "}
              <Anchor component={Link} to="/nutrition-math" onClick={closeInspector} fz="xs">
                See the nutrition math, with code
              </Anchor>
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function stepTokens(n: NodeResult) {
  const u = n.tokenUsage as Record<string, unknown> | null | undefined;
  if (!u || typeof u !== "object") return undefined;
  return Number(u.total_tokens) || (Number(u.input_tokens ?? u.prompt_tokens) || 0) + (Number(u.output_tokens ?? u.completion_tokens) || 0) || undefined;
}

function StepsTable({ r }: { r: ChainCallRecord }) {
  const steps = Object.entries(r.execution?.node_results ?? {});
  if (!steps.length) return <Text c="dimmed" fz="sm">{chainRunning(r) ? "Waiting for the first step to report…" : "No steps reported."}</Text>;
  return (
    <Table fz="xs" verticalSpacing={6} withRowBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Step</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Model</Table.Th>
          <Table.Th ta="right">Time</Table.Th>
          <Table.Th ta="right">Tokens</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {steps.map(([id, n]) => (
          <Table.Tr key={id}>
            <Table.Td ff="monospace">
              {id}
              {id === r.sinkNodeId && (
                <Badge size="xs" variant="light" color="lime" ml={6}>
                  result
                </Badge>
              )}
              {n?.error && (
                <Text fz={11} c="red">
                  {n.error}
                </Text>
              )}
            </Table.Td>
            <Table.Td>{n?.status ?? "–"}</Table.Td>
            <Table.Td ff="monospace">{typeof n?.modelUsed === "string" ? n.modelUsed : "–"}</Table.Td>
            <Table.Td ta="right" ff="monospace">
              {typeof n?.executionTime === "number" ? `${(n.executionTime / 1000).toFixed(1)} s` : "–"}
            </Table.Td>
            <Table.Td ta="right" ff="monospace">
              {n ? fmtNumber(stepTokens(n)) : "–"}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function ChainCallDetail({ r }: { r: ChainCallRecord }) {
  const lang = useCodeLang();
  const exe = r.execution;
  const tokens = exe ? executionTokens(exe) : undefined;
  const ms = Number(exe?.execution_time_ms) || r.durationMs;
  const result = exe?.status === "completed" ? sinkOutput(exe, r.sinkNodeId) : undefined;
  const items: [string, string][] = [
    ["Status", r.status],
    ["Duration", `${(ms / 1000).toFixed(1)} s`],
    ["Tokens", tokens !== undefined ? fmtNumber(tokens) : chainRunning(r) ? "counting…" : "–"],
    ["Execution ID", r.executionId ?? "–"],
    ["Polls", String(r.polls)],
    ["Cost", exe?.total_cost ? `$${Number(exe.total_cost).toFixed(4)}` : exe?.total_ai_credits_used ? `${fmtNumber(exe.total_ai_credits_used)} credits` : "–"],
  ];
  return (
    <Stack gap="md">
      <Group gap="xs" wrap="nowrap">
        <Badge color={statusColor(r)} variant="filled" style={{ flex: "none" }}>
          {statusLabel(r)}
        </Badge>
        <Badge variant="outline" color="ink" style={{ flex: "none" }}>
          POST
        </Badge>
        <Text ff="monospace" fz="xs" style={{ wordBreak: "break-all" }}>
          {executePath(r)}
        </Text>
      </Group>
      <Text fz="sm" c="dimmed">
        {r.label ?? "EasyAI chain"}: one run is two calls. Execute returns at once with an execution id, then the app polls{" "}
        <span className="pd-mono">/api/v2/chains/executions/{"{id}"}</span> until it finishes.
      </Text>
      <MetaGrid items={items} />
      {chainRunning(r) && (
        <Group gap="xs">
          <Loader size="xs" color="lime.7" />
          <Text fz="sm" c="dimmed">
            Running. This updates on every poll.
          </Text>
        </Group>
      )}
      {r.error && (
        <Text c={r.status === "stopped" ? "orange" : "red"} fz="sm">
          {r.error}
        </Text>
      )}
      <Tabs defaultValue="code" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="code">Code</Tabs.Tab>
          <Tabs.Tab value="steps">Steps</Tabs.Tab>
          <Tabs.Tab value="result">Result</Tabs.Tab>
          <Tabs.Tab value="request">Request</Tabs.Tab>
          <Tabs.Tab value="execution">Execution</Tabs.Tab>
          <Tabs.Tab value="full">Full JSON</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="code" pt="sm">
          <SegmentedControl size="xs" mb="sm" value={lang} onChange={(v) => codeLang.set(v as CodeLang)} data={CODE_LANGS} />
          <CodeBlock code={chainCode(lang, r, easyAiOrigin())} language={CODE_LANGUAGE[lang]} />
          <Alert mt="sm" variant="light" color="gray" icon={<IconLock size={16} />} p="xs">
            <Text fz="xs">
              The snippets read your key from <span className="pd-mono">EASYAI_API_KEY</span>. Keep it on your server: this demo's browser never sees one, it calls a
              proxy that adds the key.
            </Text>
          </Alert>
        </Tabs.Panel>
        <Tabs.Panel value="steps" pt="sm">
          <StepsTable r={r} />
        </Tabs.Panel>
        <Tabs.Panel value="result" pt="sm">
          {result === undefined ? (
            <Text c="dimmed" fz="sm">
              {chainRunning(r) ? "Shows once the run completes." : "No result."}
            </Text>
          ) : (
            <>
              <Text fz="xs" c="dimmed" mb={6}>
                Parsed output of {r.sinkNodeId ? <span className="pd-mono">{r.sinkNodeId}</span> : "the last step"}, the chain's result step.
              </Text>
              <JsonView value={result} maxHeight="60vh" fileName={`${fileStem(r)}-result.json`} />
            </>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="request" pt="sm">
          <JsonView value={r.requestBody} maxHeight="60vh" fileName={`${fileStem(r)}-request.json`} />
        </Tabs.Panel>
        <Tabs.Panel value="execution" pt="sm">
          {exe ? <JsonView value={exe} maxHeight="60vh" fileName={`${fileStem(r)}-execution.json`} /> : <Text c="dimmed">No execution yet</Text>}
        </Tabs.Panel>
        <Tabs.Panel value="full" pt="sm">
          <Text fz="xs" c="dimmed" mb={6}>
            Both calls as they look against EasyAI directly: URLs, headers, the request body and the final execution. The key is a placeholder.
          </Text>
          <JsonView value={chainExchange(r)} defaultMode="raw" maxHeight="60vh" fileName={`${fileStem(r)}-exchange.json`} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

export function InspectorDrawer() {
  const { open, selectedId } = useInspector();
  const calls = useCallHistory();
  const [wide, setWide] = useLocalStorage({ key: "pd-inspector-wide", defaultValue: false });
  const selected = calls.find((c) => c.id === selectedId) ?? calls[0];
  return (
    <Drawer
      opened={open}
      onClose={closeInspector}
      position="right"
      size={wide ? "min(1100px, 96vw)" : "min(640px, 100vw)"}
      title={
        <Group gap="xs" wrap="nowrap">
          <Text fw={700}>Last call inspector</Text>
          <Tooltip label={wide ? "Narrower" : "Wider"}>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setWide(!wide)} aria-label={wide ? "Make the inspector narrower" : "Make the inspector wider"}>
              {wide ? <IconArrowsDiagonalMinimize2 size={16} /> : <IconArrowsDiagonal size={16} />}
            </ActionIcon>
          </Tooltip>
          {calls.length > 0 && (
            <Button variant="subtle" color="gray" size="compact-xs" onClick={clearCalls}>
              Clear history
            </Button>
          )}
        </Group>
      }
      styles={{ body: { overflowX: "hidden" } }}
    >
      {!selected ? (
        <Text c="dimmed">Make a request or run a chain and its usage, JSON and code will appear here.</Text>
      ) : (
        <Stack gap="lg">
          {isChainCall(selected) ? <ChainCallDetail r={selected} key={selected.id} /> : <CallDetail r={selected} key={selected.id} />}
          <div>
            <Group gap={6} mb={6}>
              <IconHistory size={16} />
              <Text fw={650} fz="sm">
                History
              </Text>
            </Group>
            <Stack gap={4}>
              {calls.map((c) => (
                <UnstyledButton
                  key={c.id}
                  onClick={() => openInspector(c.id)}
                  p={6}
                  style={{ borderRadius: 8, background: c.id === selected.id ? "var(--pd-sand)" : undefined }}
                >
                  <Group gap="xs" wrap="nowrap">
                    <Badge size="xs" color={statusColor(c)} w={56}>
                      {statusLabel(c)}
                    </Badge>
                    <Text fz="xs" fw={600} style={{ flex: 1 }} truncate>
                      {callTitle(c)}
                    </Text>
                    <Text fz="xs" c="dimmed" ff="monospace">
                      {fmtNumber(callTokens(c))} tok · {fmtDuration(c.durationMs)}
                    </Text>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </div>
        </Stack>
      )}
    </Drawer>
  );
}

function LastCallSummary({ r }: { r: CallRecord }) {
  if (isChainCall(r)) {
    const inputs = Object.entries(r.requestBody.input_data).map(([k, v]) => {
      const s = typeof v === "string" ? v : JSON.stringify(v);
      return [k, s.length > 90 ? `${s.slice(0, 90)}…` : s] as [string, string];
    });
    return (
      <>
        <Text fw={700} fz="sm">
          POST {executePath(r)}
        </Text>
        <Text fz="xs" c="dimmed">
          Execution <span className="pd-mono">{r.executionId ?? "–"}</span> · {r.polls} polls
        </Text>
        <Text fw={650} fz="xs" mt={4}>
          Inputs
        </Text>
        <HeaderTable rows={inputs} />
        <Text fz="xs" c="dimmed">
          Click to open the full inspector: steps, result and code in cURL, TypeScript and Python.
        </Text>
      </>
    );
  }
  const inputHeaders = Object.entries(r.requestHeaders).filter(([k]) => !["accept", "content-type", "authorization", "passio-id"].includes(k.toLowerCase()));
  return (
    <>
      <Text fw={700} fz="sm">
        {r.method} {r.path}
      </Text>
      <Text fz="xs" c="dimmed">
        Request ID <span className="pd-mono">{r.meta?.requestId ?? "–"}</span>
      </Text>
      <Text fw={650} fz="xs" mt={4}>
        Input headers
      </Text>
      <HeaderTable rows={inputHeaders.map(([k, v]) => [k, v.length > 90 ? `${v.slice(0, 90)}…` : v])} />
      <Text fz="xs" c="dimmed">
        Click to open the full inspector: JSON, all response headers, cURL / TypeScript.
      </Text>
    </>
  );
}

/** Floating summary of the latest call; hover for details, click for the full inspector. */
export function LastCallBar() {
  const calls = useCallHistory();
  const r = calls[0];
  if (!r) return null;
  return (
    <Box style={{ position: "fixed", right: 20, bottom: 20, zIndex: 150 }}>
      <HoverCard width={420} position="top-end" shadow="lg" openDelay={150} withinPortal>
        <HoverCard.Target>
          <Button
            onClick={() => openInspector(null)}
            color="ink.8"
            radius="xl"
            size="md"
            rightSection={<IconChevronUp size={16} />}
            leftSection={<Badge size="sm" color={statusColor(r)}>{statusLabel(r)}</Badge>}
            styles={{ root: { boxShadow: "0 8px 30px rgba(11,19,32,.35)" } }}
          >
            <Text fz="sm" fw={600} maw={220} truncate>
              {callTitle(r)}
            </Text>
            <Text fz="xs" c="var(--pd-ink-muted)" ml={10} ff="monospace">
              {fmtNumber(callTokens(r))} tok · {fmtDuration(r.durationMs)}
            </Text>
          </Button>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack gap={8}>
            <LastCallSummary r={r} />
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    </Box>
  );
}
